import React from 'react'
import { useTranslation } from 'react-i18next'
import { User, Building2, FileText, Receipt, Clock } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { formatRelativeTime } from '@/lib/format'
import { ActivityItem } from '@/api/types'
import { tw } from '@/lib/rtl'

interface ActivityFeedProps {
  title: string
  description?: string
  activities: ActivityItem[]
  maxItems?: number
  showTimestamp?: boolean
  className?: string
}

/**
 * Activity feed widget for displaying recent system activities
 * Shows user actions with icons, descriptions, and timestamps
 */
export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  title,
  description,
  activities,
  maxItems = 5,
  showTimestamp = true,
  className,
}) => {
  const { t } = useTranslation('common')

  const getActivityIcon = (type: ActivityItem['type']) => {
    const iconMap = {
      Employee: User,
      Customer: Building2,
      Quotation: FileText,
      Invoice: Receipt,
    }
    const Icon = iconMap[type] || Clock
    return <Icon className="h-4 w-4" />
  }

  const getActivityColor = (type: ActivityItem['type']) => {
    const colorMap = {
      Employee: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300',
      Customer: 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300',
      Quotation: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-300',
      Invoice: 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300',
    }
    return colorMap[type] || 'bg-gray-100 text-gray-600 dark:bg-gray-900 dark:text-gray-300'
  }

  const displayedActivities = activities.slice(0, maxItems)

  if (displayedActivities.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Clock className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              {t('common.noData')}
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {displayedActivities.map((activity) => (
            <div
              key={activity.id}
              className="flex items-start space-x-3 rtl:space-x-reverse"
            >
              <div className={`flex h-8 w-8 items-center justify-center rounded-full ${getActivityColor(activity.type)}`}>
                {getActivityIcon(activity.type)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-foreground">
                    {activity.description}
                  </p>
                  {showTimestamp && (
                    <p className={`text-xs text-muted-foreground ${tw.ms('2')} flex-shrink-0`}>
                      {formatRelativeTime(activity.timestamp)}
                    </p>
                  )}
                </div>
                
                <div className="flex items-center mt-1 space-x-2 rtl:space-x-reverse">
                  <Badge variant="outline" className="text-xs">
                    {activity.type}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    by {activity.userName}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {activities.length > maxItems && (
          <div className="mt-4 text-center">
            <p className="text-xs text-muted-foreground">
              {t('common.total')}: {activities.length} {t('common.items')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
} 